import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Skeleton,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  DataScienceJob,
  DataScienceJobApi,
  TaskStatus,
} from "../../api/DataScienceJobApi";
import { getOrganizations } from "../../api/Services";
import { getSelectedOrganizationId, hasWorkingOrganization } from "../../api/Helper";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import RecommendationFlowConfigCard from "./RecommendationFlowConfigCard";
import JobOriginIndicator from "./JobOriginIndicator";
import RecommendationJobIndicator from "./RecommendationJobIndicator";
import {
  getDataScienceJobTypes,
  isRecommendationJobType,
} from "./dataScienceJobTypes";
import { renderDataScienceStatusTag } from "./dataScienceStatusTag";
import {
  downloadTaskLogsFromServer,
  TASK_LOGS_MODAL_WIDTH,
  taskLogsModalBodyStyle,
  taskLogsModalStyle,
  taskLogsScrollContainerStyle,
} from "./taskLogsHelper";
import TaskLogLines, { canViewTaskLogs } from "./TaskLogLines";
import TaskLogsModalTitle from "./TaskLogsModalTitle";
import { RecommendationRunApi } from "../../api/RecommendationRunApi";
import {
  formatDuration,
  formatDurationSeconds,
} from "./formatDuration";
import FaissIndexes from "../faissindexes/FaissIndexes";
import DemandForecastArtifacts from "../demandforecastartifacts/DemandForecastArtifacts";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StatisticValueSkeleton: React.FC<{ width?: number }> = ({ width = 56 }) => (
  <Skeleton.Input active size="small" style={{ width, height: 28, minWidth: width }} />
);

type DashboardStatisticProps = {
  title: string;
  loading?: boolean;
  value: string | number;
  prefix?: React.ReactNode;
  valueStyle?: React.CSSProperties;
  skeletonWidth?: number;
};

const DashboardStatistic: React.FC<DashboardStatisticProps> = ({
  title,
  loading = false,
  value,
  prefix,
  valueStyle,
  skeletonWidth = 56,
}) => (
  <Card>
    {loading ? (
      <>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {title}
        </Text>
        <div style={{ marginTop: 8 }}>
          <StatisticValueSkeleton width={skeletonWidth} />
        </div>
      </>
    ) : (
      <Statistic title={title} value={value} prefix={prefix} valueStyle={valueStyle} />
    )}
  </Card>
);

const DataScienceJobsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname.includes("/faiss-indexes")
    ? "faiss"
    : location.pathname.includes("/demand-forecast-models")
    ? "models"
    : "jobs";
  const jobTypes = useMemo(() => getDataScienceJobTypes(t), [t]);

  const [overviewJobs, setOverviewJobs] = useState<DataScienceJob[]>([]);
  const [recommendationSelections, setRecommendationSelections] = useState<
    Record<string, boolean>
  >({});
  const [tableJobs, setTableJobs] = useState<DataScienceJob[]>([]);
  const [tasks, setTasks] = useState<TaskStatus[]>([]);
  const [organizations, setOrganizations] = useState<Record<number, string>>({});
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(true);

  const [jobTypeFilter, setJobTypeFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [nameSearch, setNameSearch] = useState("");
  const [jobsPagination, setJobsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [taskJobTypeFilter, setTaskJobTypeFilter] = useState<string | undefined>();
  const [taskStatusFilter, setTaskStatusFilter] = useState<string | undefined>();
  const [tasksPagination, setTasksPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [selectedTaskJobLabel, setSelectedTaskJobLabel] = useState("");
  const [logs, setLogs] = useState<Array<{ timestamp: number; message: string }>>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDownloading, setLogsDownloading] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [executingJobId, setExecutingJobId] = useState<number | null>(null);

  const workingOrganizationId =
    hasWorkingOrganization() && getSelectedOrganizationId()
      ? Number(getSelectedOrganizationId())
      : undefined;

  const jobTypeLabel = useCallback(
    (type?: string) =>
      jobTypes.find((item) => item.jobType === type)?.label || type || "-",
    [jobTypes]
  );

  const loadOrganizations = async () => {
    try {
      const orgs = await getOrganizations();
      const orgMap: Record<number, string> = {};
      orgs.forEach((org) => {
        orgMap[org.id] = org.name;
      });
      setOrganizations(orgMap);
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
  };

  const usesLocalJobFilters =
    statusFilter !== "all" || nameSearch.trim().length > 0;

  const activeFilterParam =
    activeFilter === "active" ? true : activeFilter === "inactive" ? false : undefined;

  const applyLocalJobFilters = useCallback(
    (jobsToFilter: DataScienceJob[]) =>
      jobsToFilter.filter((job) => {
        if (statusFilter !== "all" && job.last_status !== statusFilter) return false;
        if (
          nameSearch.trim() &&
          !job.job_name.toLowerCase().includes(nameSearch.trim().toLowerCase())
        ) {
          return false;
        }
        return true;
      }),
    [statusFilter, nameSearch]
  );

  const loadOverviewJobs = async (withLoading = false) => {
    if (!workingOrganizationId) {
      setOverviewJobs([]);
      setRecommendationSelections({});
      setOverviewLoading(false);
      return;
    }

    try {
      if (withLoading) {
        setOverviewLoading(true);
      }
      const [jobsData, selections] = await Promise.all([
        DataScienceJobApi.listJobs({
          organization_id: workingOrganizationId,
          page: 1,
          pageSize: 500,
        }),
        RecommendationRunApi.getJobSelections(workingOrganizationId).catch(() => []),
      ]);

      if (Array.isArray(jobsData)) {
        setOverviewJobs(jobsData);
      } else if (jobsData && typeof jobsData === "object") {
        setOverviewJobs(jobsData.items || jobsData.data || jobsData.jobs || []);
      } else {
        setOverviewJobs([]);
      }

      const selectionMap: Record<string, boolean> = {};
      selections.forEach((selection) => {
        if (selection.job_type && selection.job_id) {
          selectionMap[selection.job_type] = true;
        }
      });
      setRecommendationSelections(selectionMap);
    } catch (error) {
      message.error(t("DataScienceJobs-LoadJobsError"));
    } finally {
      if (withLoading) {
        setOverviewLoading(false);
      }
    }
  };

  const loadTableJobs = async (page = 1, pageSize = 10) => {
    if (!workingOrganizationId) {
      setTableJobs([]);
      setJobsPagination((prev) => ({ ...prev, current: page, pageSize, total: 0 }));
      return;
    }

    try {
      setTableLoading(true);

      if (usesLocalJobFilters) {
        const jobsData = await DataScienceJobApi.listJobs({
          organization_id: workingOrganizationId,
          job_type: jobTypeFilter,
          is_active: activeFilterParam,
          page: 1,
          pageSize: 500,
        });

        let allJobs: DataScienceJob[] = [];
        if (Array.isArray(jobsData)) {
          allJobs = jobsData;
        } else if (jobsData && typeof jobsData === "object") {
          allJobs = jobsData.items || jobsData.data || jobsData.jobs || [];
        }

        const filtered = applyLocalJobFilters(allJobs);
        const offset = (page - 1) * pageSize;
        setTableJobs(filtered.slice(offset, offset + pageSize));
        setJobsPagination({ current: page, pageSize, total: filtered.length });
        return;
      }

      const jobsData = await DataScienceJobApi.listJobs({
        organization_id: workingOrganizationId,
        job_type: jobTypeFilter,
        is_active: activeFilterParam,
        page,
        pageSize,
      });

      let rows: DataScienceJob[] = [];
      let total = 0;
      if (Array.isArray(jobsData)) {
        rows = jobsData;
        total = jobsData.length;
      } else if (jobsData && typeof jobsData === "object") {
        rows = jobsData.items || jobsData.data || jobsData.jobs || [];
        total = jobsData.total ?? rows.length;
      }

      setTableJobs(rows);
      setJobsPagination({ current: page, pageSize, total });
    } catch (error) {
      message.error(t("DataScienceJobs-LoadJobsError"));
    } finally {
      setTableLoading(false);
    }
  };

  const loadTasks = async (page = 1, pageSize = 10, withLoading = true) => {
    if (!workingOrganizationId) {
      setTasks([]);
      setTasksPagination((prev) => ({ ...prev, current: page, pageSize, total: 0 }));
      setTasksLoading(false);
      return;
    }

    try {
      if (withLoading) {
        setTasksLoading(true);
      }
      const tasksData = await DataScienceJobApi.listTasks({
        organization_id: workingOrganizationId,
        job_type: taskJobTypeFilter,
        status: taskStatusFilter,
        page,
        pageSize,
      });

      let rows: TaskStatus[] = [];
      let total = 0;
      if (Array.isArray(tasksData)) {
        rows = tasksData;
        total = tasksData.length;
      } else if (tasksData && typeof tasksData === "object") {
        rows = tasksData.items || tasksData.data || tasksData.tasks || [];
        total = tasksData.total || rows.length;
      }

      setTasks(rows);
      setTasksPagination({ current: page, pageSize, total });
    } catch (error) {
      message.error(t("DataScienceJobs-LoadTasksError"));
    } finally {
      if (withLoading) {
        setTasksLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOrganizations();
    loadOverviewJobs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingOrganizationId]);

  useEffect(() => {
    loadTableJobs(1, jobsPagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workingOrganizationId,
    jobTypeFilter,
    activeFilter,
    statusFilter,
    nameSearch,
  ]);

  useEffect(() => {
    loadTasks(1, tasksPagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingOrganizationId, taskJobTypeFilter, taskStatusFilter]);

  useEffect(() => {
    if (!tasks.some((task) => task.status === "running")) {
      return;
    }

    const intervalId = window.setInterval(() => {
      loadOverviewJobs(false);
      loadTableJobs(jobsPagination.current, jobsPagination.pageSize);
      loadTasks(tasksPagination.current, tasksPagination.pageSize, false);
    }, 5000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tasks,
    jobsPagination.current,
    jobsPagination.pageSize,
    tasksPagination.current,
    tasksPagination.pageSize,
    workingOrganizationId,
  ]);

  const jobsByType = useMemo(() => {
    const counts: Record<string, number> = {};
    overviewJobs.forEach((job) => {
      counts[job.job_type] = (counts[job.job_type] || 0) + 1;
    });
    return counts;
  }, [overviewJobs]);

  const activeJobsByType = useMemo(() => {
    const counts: Record<string, number> = {};
    overviewJobs.forEach((job) => {
      if (job.is_active) {
        counts[job.job_type] = (counts[job.job_type] || 0) + 1;
      }
    });
    return counts;
  }, [overviewJobs]);

  const lastFinishedTask = useMemo(() => {
    return [...tasks]
      .filter(
        (task) =>
          task.completed_at &&
          (task.status === "success" || task.status === "failed")
      )
      .sort(
        (a, b) =>
          new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime()
      )[0];
  }, [tasks]);

  const lastStartedTask = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aTime = new Date(a.started_at || a.created_at).getTime();
      const bTime = new Date(b.started_at || b.created_at).getTime();
      return bTime - aTime;
    })[0];
  }, [tasks]);

  const stats = useMemo(
    () => ({
      totalJobs: overviewJobs.length,
      activeJobs: overviewJobs.filter((job) => job.is_active).length,
      runningTasks: tasks.filter((task) => task.status === "running").length,
      failedTasks: tasks.filter((task) => task.status === "failed").length,
    }),
    [overviewJobs, tasks]
  );

  const formatDateTime = (value?: string) =>
    value ? format(new Date(value), "dd/MM/yyyy HH:mm", { locale: es }) : "-";

  const renderJobDuration = (job: DataScienceJob) => {
    if (job.last_duration_seconds != null) {
      return formatDurationSeconds(job.last_duration_seconds);
    }
    if (job.last_started_at) {
      return formatDuration(
        job.last_started_at,
        job.last_completed_at,
        job.last_status === "running" ? t("DataScienceJobs-DurationRunning") : undefined
      );
    }
    return "-";
  };

  const handleViewLogs = async (task: TaskStatus) => {
    setSelectedTaskId(task.task_id);
    setSelectedTaskJobLabel(jobTypeLabel(task.job_type));
    setLogsModalVisible(true);
    setLogs([]);
    setNextToken(undefined);
    await loadLogs(task.task_id);
  };

  const loadLogs = async (taskId: string, loadMore = false) => {
    try {
      setLogsLoading(true);
      const response = await DataScienceJobApi.getTaskLogs(
        taskId,
        loadMore ? nextToken : undefined,
        200
      );
      const hasMore =
        Boolean(response.nextToken) && response.events.length > 0;

      if (loadMore) {
        if (response.events.length > 0) {
          setLogs((prev) => [...prev, ...response.events]);
        }
      } else {
        setLogs(response.events);
      }
      setNextToken(hasMore ? response.nextToken : undefined);
    } catch (error) {
      message.error(t("DataScienceJobs-LoadLogsError"));
    } finally {
      setLogsLoading(false);
    }
  };

  const formatLogTimestamp = (timestamp: number) =>
    format(new Date(timestamp), "dd/MM/yyyy HH:mm:ss", { locale: es });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom && nextToken && !logsLoading && selectedTaskId) {
      loadLogs(selectedTaskId, true);
    }
  };

  const handleDownloadLogs = async () => {
    if (!selectedTaskId) return;

    try {
      setLogsDownloading(true);
      await downloadTaskLogsFromServer(selectedTaskId);
      message.success(t("Common-DownloadSuccessMessage"));
    } catch (error) {
      message.error(t("DataScienceJobs-DownloadLogsError"));
    } finally {
      setLogsDownloading(false);
    }
  };

  const isJobRunning = useCallback(
    (job: DataScienceJob) => {
      if (job.last_status === "running") return true;
      return tasks.some(
        (task) =>
          task.status === "running" &&
          (task.bright_id === job.id || task.brightId === job.id)
      );
    },
    [tasks]
  );

  const getJobForTask = useCallback(
    (task: TaskStatus): DataScienceJob | undefined => {
      const jobId = task.bright_id ?? task.brightId;
      if (jobId != null) {
        return overviewJobs.find((job) => job.id === jobId);
      }

      return overviewJobs.find(
        (job) =>
          job.job_name === task.job_name &&
          (!task.job_type || job.job_type === task.job_type)
      );
    },
    [overviewJobs]
  );

  const handleExecuteJob = async (job: DataScienceJob) => {
    try {
      setExecutingJobId(job.id);
      await DataScienceJobApi.executeJob(job.id);
      message.success(t("DataScienceJobs-ExecuteJobSuccess"));
      await loadOverviewJobs();
      await loadTableJobs(jobsPagination.current, jobsPagination.pageSize);
      await loadTasks(tasksPagination.current, tasksPagination.pageSize);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("DataScienceJobs-ExecuteJobError"))
      );
    } finally {
      setExecutingJobId(null);
    }
  };

  const renderExecuteButton = (job: DataScienceJob, showLabel = false) => {
    const running = isJobRunning(job);
    const disabled = !job.is_active || running;
    const button = (
      <Button
        type="primary"
        size="small"
        icon={<PlayCircleOutlined />}
        disabled={disabled}
        loading={executingJobId === job.id}
      >
        {showLabel ? t("DataScienceJobs-Execute") : null}
      </Button>
    );

    if (disabled) {
      return (
        <Tooltip
          title={
            !job.is_active
              ? t("DataScienceJobs-ExecuteInactiveHelp")
              : t("DataScienceJobs-DurationRunning")
          }
        >
          {button}
        </Tooltip>
      );
    }

    const executeWarningKey = jobTypes.find(
      (item) => item.jobType === job.job_type
    )?.executeWarningKey;

    return (
      <Popconfirm
        title={
          executeWarningKey ? (
            <div style={{ maxWidth: 320 }}>
              <div>{t("DataScienceJobs-ConfirmExecute")}</div>
              <div style={{ marginTop: 4, color: "#faad14" }}>
                {t(executeWarningKey)}
              </div>
            </div>
          ) : (
            t("DataScienceJobs-ConfirmExecute")
          )
        }
        onConfirm={() => handleExecuteJob(job)}
        okText={t("Common-Yes")}
        cancelText={t("Common-No")}
      >
        {button}
      </Popconfirm>
    );
  };

  const jobColumns = [
    {
      title: t("DataScienceJobs-JobName"),
      dataIndex: "job_name",
      key: "job_name",
    },
    {
      title: t("DataScienceJobs-FilterJobType"),
      dataIndex: "job_type",
      key: "job_type",
      render: (type: string) => jobTypeLabel(type),
    },
    {
      title: t("DataScienceJobs-Organization"),
      dataIndex: "organization_id",
      key: "organization_id",
      render: (orgId?: number) =>
        orgId != null ? organizations[orgId] || `ID: ${orgId}` : "-",
    },
    {
      title: t("DataScienceJobs-IsActive"),
      dataIndex: "is_active",
      key: "is_active",
      render: (active: boolean) =>
        active ? (
          <Text type="success">{t("Common-Yes")}</Text>
        ) : (
          <Text type="secondary">{t("Common-No")}</Text>
        ),
    },
    {
      title: t("DataScienceJobs-LastStatus"),
      dataIndex: "last_status",
      key: "last_status",
      render: (status?: string) => renderDataScienceStatusTag(status, t),
    },
    {
      title: t("DataScienceJobs-LastExecution"),
      dataIndex: "last_execution",
      key: "last_execution",
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: t("DataScienceJobs-Duration"),
      key: "duration",
      render: (_: unknown, record: DataScienceJob) => renderJobDuration(record),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 200,
      render: (_: unknown, record: DataScienceJob) => (
        <Space size="small">
          <Tooltip title={t("DataScienceJobs-Execute")}>
            {renderExecuteButton(record)}
          </Tooltip>
          <Tooltip title={t("DataScienceJobs-View")}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/datasciencejobs/${record.id}/view`)}
            />
          </Tooltip>
          <Tooltip title={t("DataScienceJobs-OpenJobType")}>
            <Button
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={() => navigate(`/admin/datasciencejobs/${record.job_type}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const taskColumns = [
    {
      title: t("DataScienceJobs-FilterJobType"),
      dataIndex: "job_type",
      key: "job_type",
      render: (type?: string) => jobTypeLabel(type),
    },
    {
      title: t("DataScienceJobs-JobName"),
      dataIndex: "job_name",
      key: "job_name",
    },
    {
      title: t("DataScienceJobs-Status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => renderDataScienceStatusTag(status, t),
    },
    {
      title: t("DataScienceJobs-Message"),
      dataIndex: "message",
      key: "message",
      ellipsis: true,
    },
    {
      title: t("DataScienceJobs-StartedAt"),
      dataIndex: "started_at",
      key: "started_at",
      render: (_: unknown, record: TaskStatus) =>
        formatDateTime(record.started_at || record.created_at),
    },
    {
      title: t("DataScienceJobs-CompletedAt"),
      dataIndex: "completed_at",
      key: "completed_at",
      render: (value?: string) => formatDateTime(value),
    },
    {
      title: t("DataScienceJobs-Duration"),
      key: "duration",
      render: (_: unknown, record: TaskStatus) =>
        formatDuration(
          record.started_at || record.created_at,
          record.completed_at,
          record.status === "running" ? t("DataScienceJobs-DurationRunning") : undefined
        ),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 160,
      render: (_: unknown, record: TaskStatus) => {
        const job = getJobForTask(record);

        return (
          <Space size="small">
            {job ? (
              <Tooltip title={t("DataScienceJobs-Execute")}>
                {renderExecuteButton(job)}
              </Tooltip>
            ) : null}
            {canViewTaskLogs(record) ? (
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewLogs(record)}
              >
                {t("DataScienceJobs-Logs")}
              </Button>
            ) : null}
            {!job && !canViewTaskLogs(record) ? "-" : null}
          </Space>
        );
      },
    },
  ];

  return (
    <AdminValidation>
      <div style={{ padding: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              {t("DataScienceJobs-DashboardTitle")}
            </Title>
            <Text type="secondary">{t("DataScienceJobs-DashboardDescription")}</Text>
          </div>
          {activeTab === "jobs" && (
            <Space>
              <RecommendationFlowConfigCard organizationId={workingOrganizationId} />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadOverviewJobs(true);
                  loadTableJobs(jobsPagination.current, jobsPagination.pageSize);
                  loadTasks(tasksPagination.current, tasksPagination.pageSize, true);
                }}
              >
                {t("Common-Refresh")}
              </Button>
            </Space>
          )}
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            navigate(
              key === "faiss"
                ? "/admin/datasciencejobs/faiss-indexes"
                : key === "models"
                ? "/admin/datasciencejobs/demand-forecast-models"
                : "/admin/datasciencejobs"
            );
          }}
          style={{ marginBottom: 8 }}
        >
          <TabPane tab={t("DataScienceJobs-TabJobs")} key="jobs" />
          <TabPane tab={t("FaissIndexes-TabTitle")} key="faiss" />
          <TabPane tab={t("DemandForecastArtifacts-TabTitle")} key="models" />
        </Tabs>

        {activeTab === "faiss" ? (
          <FaissIndexes embedded />
        ) : activeTab === "models" ? (
          <DemandForecastArtifacts embedded />
        ) : (
          <>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <DashboardStatistic
              title={t("DataScienceJobs-TotalJobs")}
              loading={overviewLoading}
              value={stats.totalJobs}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <DashboardStatistic
              title={t("Common-Active")}
              loading={overviewLoading}
              value={stats.activeJobs}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <DashboardStatistic
              title={t("DataScienceJobs-Statuses.running")}
              loading={tasksLoading}
              value={stats.runningTasks}
              prefix={<SyncOutlined spin={!tasksLoading && stats.runningTasks > 0} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <DashboardStatistic
              title={t("DataScienceJobs-Statuses.failed")}
              loading={tasksLoading}
              value={stats.failedTasks}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={4}>
            <DashboardStatistic
              title={t("DataScienceJobs-LastStartedRun")}
              loading={tasksLoading}
              value={
                lastStartedTask
                  ? formatDateTime(
                      lastStartedTask.started_at || lastStartedTask.created_at
                    )
                  : "-"
              }
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 14 }}
              skeletonWidth={128}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={4}>
            <DashboardStatistic
              title={t("DataScienceJobs-LastFinishedRun")}
              loading={tasksLoading}
              value={
                lastFinishedTask ? formatDateTime(lastFinishedTask.completed_at) : "-"
              }
              prefix={<CheckCircleOutlined />}
              valueStyle={{ fontSize: 14 }}
              skeletonWidth={128}
            />
          </Col>
        </Row>

        <Card title={t("DataScienceJobs-OpenJobType")} style={{ marginBottom: 24 }}>
          <Row gutter={[12, 12]}>
            {jobTypes.map((jobType) => {
              const configuredCount = jobsByType[jobType.jobType] || 0;
              const activeCount = activeJobsByType[jobType.jobType] || 0;

              return (
              <Col xs={24} sm={12} md={8} lg={6} xl={4} key={jobType.key}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => navigate(`/admin/datasciencejobs/${jobType.jobType}`)}
                  style={{ cursor: "pointer", height: "100%", position: "relative" }}
                >
                  <Space direction="vertical" size={4}>
                    <Space>
                      <Text strong>{jobType.label}</Text>
                      <JobOriginIndicator
                        origin={jobType.origin}
                        size={13}
                        tooltipTitle={jobType.label}
                      />
                    </Space>
                    {overviewLoading ? (
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 120, height: 16, marginTop: 4 }}
                      />
                    ) : (
                      <Space direction="vertical" size={2}>
                        {jobType.singletonSystemJob ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("DataScienceJobs-SingletonSystemJobReady")}
                          </Text>
                        ) : (
                          <>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t("DataScienceJobs-JobsConfiguredCount", {
                                count: configuredCount,
                              })}
                            </Text>
                            {configuredCount > 0 && (
                              <Text
                                style={{
                                  fontSize: 12,
                                  color: activeCount > 0 ? "#52c41a" : "#bfbfbf",
                                }}
                              >
                                {t("DataScienceJobs-ActiveJobsCount", {
                                  count: activeCount,
                                })}
                              </Text>
                            )}
                          </>
                        )}
                      </Space>
                    )}
                  </Space>
                  {isRecommendationJobType(jobType.jobType) && (
                    <div style={{ position: "absolute", bottom: 8, right: 8 }}>
                      <RecommendationJobIndicator
                        configured={!!recommendationSelections[jobType.jobType]}
                        size={16}
                      />
                    </div>
                  )}
                </Card>
              </Col>
              );
            })}
          </Row>
        </Card>

        <Card title={t("DataScienceJobs-AllJobs")} style={{ marginBottom: 24 }}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder={t("DataScienceJobs-FilterJobType")}
              style={{ width: 220 }}
              value={jobTypeFilter}
              onChange={setJobTypeFilter}
              options={jobTypes.map((item) => ({
                value: item.jobType,
                label: item.label,
              }))}
            />
            <Select
              style={{ width: 160 }}
              value={activeFilter}
              onChange={setActiveFilter}
              options={[
                { value: "all", label: t("DataScienceJobs-FilterActiveAll") },
                { value: "active", label: t("DataScienceJobs-FilterActive") },
                { value: "inactive", label: t("DataScienceJobs-FilterInactive") },
              ]}
            />
            <Select
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: t("DataScienceJobs-FilterStatusAll") },
                { value: "running", label: t("DataScienceJobs-Statuses.running") },
                { value: "success", label: t("DataScienceJobs-Statuses.success") },
                { value: "failed", label: t("DataScienceJobs-Statuses.failed") },
              ]}
            />
            <Input.Search
              allowClear
              placeholder={t("DataScienceJobs-SearchJobName")}
              style={{ width: 240 }}
              onSearch={setNameSearch}
              onChange={(e) => {
                if (!e.target.value) setNameSearch("");
              }}
            />
          </Space>
          <Table
            rowKey="id"
            loading={tableLoading}
            columns={jobColumns}
            dataSource={tableJobs}
            pagination={{
              current: jobsPagination.current,
              pageSize: jobsPagination.pageSize,
              total: jobsPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("Common-PaginationOf")} ${total} ${t(
                  "Common-PaginationItems"
                )}`,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                loadTableJobs(page, pageSize);
              },
              onShowSizeChange: (_, size) => {
                loadTableJobs(1, size);
              },
              locale: {
                items_per_page: t("Common-PaginationPerPage"),
                jump_to: t("Common-PaginationGoTo"),
                page: t("Common-PaginationPage"),
              },
            }}
            scroll={{ x: 1100 }}
          />
        </Card>

        <Card title={t("DataScienceJobs-RecentExecutions")}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder={t("DataScienceJobs-FilterJobType")}
              style={{ width: 220 }}
              value={taskJobTypeFilter}
              onChange={setTaskJobTypeFilter}
              options={jobTypes.map((item) => ({
                value: item.jobType,
                label: item.label,
              }))}
            />
            <Select
              allowClear
              placeholder={t("DataScienceJobs-FilterStatus")}
              style={{ width: 160 }}
              value={taskStatusFilter}
              onChange={setTaskStatusFilter}
              options={[
                { value: "running", label: t("DataScienceJobs-Statuses.running") },
                { value: "success", label: t("DataScienceJobs-Statuses.success") },
                { value: "failed", label: t("DataScienceJobs-Statuses.failed") },
              ]}
            />
          </Space>
          <Table
            rowKey="task_id"
            loading={tasksLoading}
            columns={taskColumns}
            dataSource={tasks}
            scroll={{ x: 1100 }}
            pagination={{
              current: tasksPagination.current,
              pageSize: tasksPagination.pageSize,
              total: tasksPagination.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => loadTasks(page, pageSize),
            }}
          />
        </Card>

        <Modal
          title={
            <TaskLogsModalTitle
              title={`${t("DataScienceJobs-TaskLogs")}${selectedTaskJobLabel ? ` - ${selectedTaskJobLabel}` : ""} - ${selectedTaskId}`}
              onDownload={handleDownloadLogs}
              downloading={logsDownloading}
              downloadDisabled={!selectedTaskId}
              downloadTooltip={t("Common-Download")}
            />
          }
          visible={logsModalVisible}
          onCancel={() => setLogsModalVisible(false)}
          width={TASK_LOGS_MODAL_WIDTH}
          style={taskLogsModalStyle}
          bodyStyle={taskLogsModalBodyStyle}
          footer={[
            <Button key="close" onClick={() => setLogsModalVisible(false)}>
              {t("Common-Close")}
            </Button>,
          ]}
        >
          <div style={taskLogsScrollContainerStyle} onScroll={handleScroll}>
            <TaskLogLines
              logs={logs}
              logsLoading={logsLoading}
              nextToken={nextToken}
              formatTimestamp={formatLogTimestamp}
              t={t}
            />
          </div>
        </Modal>
          </>
        )}
      </div>
    </AdminValidation>
  );
};

export default DataScienceJobsDashboard;
